// 📂 src/types/system/icons.ts

/** Lista de todos los iconos soportados por InputIconByKey */
export type IconKey =
    | 'dollar'
    | 'bill'
    | 'coin'
    | 'coins'
    | 'cash'
    | 'document'
    | 'calendar'
    | 'user'
    | 'hash'
    | 'address'
    | 'phone'
    | 'email'
    | 'briefcase'
    | 'idCard'
    | 'edit'
    | 'truck'
    | 'more'
    | 'purchase'
    | 'settled'
    | 'pendingPurchase'
    | 'product'
    | 'category'
    | 'unit'
    | 'unitMeasure'
    | 'supplier'
    | 'stock'
    | 'barcode'
    | 'invoice'
    | 'searchInvoice'
    | 'payment'
    | 'creditCard'
    | 'transfer'
    | 'company'
    | 'discount'
    | 'percentage'
    | 'security'
    | 'list'
    | 'listOrdered'
    | 'files'
    | 'table'
    | 'check'
    | 'pause'
    | 'status'
    | 'service'
    | 'branch'
    | 'arrowDown'
    | 'arrowUp'
    | 'realtime'
    | 'channel'
    | 'channels'
    | 'notification'
    | 'notifications'
    | 'bell'
    | 'bellRing'
    | 'activity'
    | 'connected'
    | 'disconnected'
    | 'event'
    | 'events'
    | 'message'
    | 'priceDown'
    | 'priceUp'
    | 'code'
    | 'printer'
    | 'network'
    | 'port'
    | 'search'
    | 'filter'
    | 'building'
    | 'file'
    | 'alert'
    | 'qr'
    | 'layout'
    | 'scissors'
    | 'key'
    | 'lock'
    | 'keyRound'
    | 'settings'
    | 'route'
    | 'categories'
    | 'yes'
    | 'userCheck'
    | 'UserPlus'
    | 'clock'
    | 'timer'
    | 'shield'
    | 'server'
    | 'tag'
    | 'id'
    | 'layers'
    | 'scale'
    | 'ruler'
    | 'box'
    | 'stack'
    | 'archive'
    | 'store'
    | 'globe'
    | 'no'
    | 'note'
    | 'terminal'
    | 'cashRegister'
    | 'cashSession'
    | 'cashOpening'
    | 'cashClosing'
    | 'calculator'
    | 'cashAudit'
    | 'warning'
    | 'alertTriangle'
    | 'refresh'
    | 'reload'
    | 'sync'
    | 'inventory'
    | 'warehouse'
    // --- 🍽️ Restaurante / alimentos y bebidas ---
    | 'utensils'
    | 'utensilsCrossed'
    | 'chefHat'
    | 'platter'
    | 'roomService'
    | 'drinks'
    | 'coffee'
    | 'tea'
    | 'wine'
    | 'beer'
    | 'cocktail'
    | 'soda'
    | 'water'
    | 'juice'
    | 'milk'
    | 'soup'
    | 'salad'
    | 'pizza'
    | 'sandwich'
    | 'burger'
    | 'meat'
    | 'ham'
    | 'chicken'
    | 'fish'
    | 'seafood'
    | 'egg'
    | 'breakfast'
    | 'bread'
    | 'bakery'
    | 'cookie'
    | 'cake'
    | 'dessert'
    | 'iceCream'
    | 'iceCreamBowl'
    | 'candy'
    | 'donut'
    | 'snack'
    | 'popcorn'
    | 'nuts'
    | 'fruit'
    | 'cherry'
    | 'grape'
    | 'citrus'
    | 'vegetables'
    | 'carrot'
    | 'pepper'
    | 'sauce'
    | 'grain'
    | 'grill'
    | 'spicy'
    | 'snowflake'
    | 'package'
    | 'vegan'
    | 'vegetarian';

/**
 * Subconjunto de iconos pensado para categorías del menú de un restaurante.
 * Se usa en el selector con buscador del formulario de categorías.
 * Cada entrada lleva palabras clave en español para que el buscador
 * encuentre el icono aunque el `key` esté en inglés.
 */
export interface RestaurantIconOption {
    key: IconKey;
    label: string;
    keywords: string;
}

export const RESTAURANT_MENU_ICON_OPTIONS: RestaurantIconOption[] = [
    { key: 'utensils', label: 'Cubiertos', keywords: 'cubiertos tenedor cuchara comida platillo general' },
    { key: 'utensilsCrossed', label: 'Cubiertos cruzados', keywords: 'cubiertos cruzados restaurante comida' },
    { key: 'chefHat', label: 'Gorro de chef', keywords: 'chef cocina gorro cocinero especial del dia' },
    { key: 'platter', label: 'Charola / bandeja', keywords: 'charola bandeja servicio mesero platillo fuerte' },
    { key: 'soup', label: 'Sopa', keywords: 'sopa caldo crema entrada' },
    { key: 'salad', label: 'Ensalada', keywords: 'ensalada verde vegetales entrada saludable' },
    { key: 'pizza', label: 'Pizza', keywords: 'pizza italiana rebanada' },
    { key: 'sandwich', label: 'Sándwich', keywords: 'sandwich bocadillo torta baguette' },
    { key: 'burger', label: 'Hamburguesa', keywords: 'hamburguesa burger comida rapida' },
    { key: 'meat', label: 'Carne', keywords: 'carne res bistec steak parrilla asado' },
    { key: 'ham', label: 'Jamón / cerdo', keywords: 'jamon cerdo pernil embutido' },
    { key: 'chicken', label: 'Pollo', keywords: 'pollo ave muslo alitas' },
    { key: 'fish', label: 'Pescado', keywords: 'pescado marisco mar ceviche' },
    { key: 'seafood', label: 'Mariscos', keywords: 'mariscos camaron marisco mar' },
    { key: 'egg', label: 'Huevo', keywords: 'huevo desayuno' },
    { key: 'breakfast', label: 'Desayuno', keywords: 'desayuno huevo frito almuerzo' },
    { key: 'bread', label: 'Pan', keywords: 'pan panaderia bolleria croissant' },
    { key: 'bakery', label: 'Panadería', keywords: 'panaderia reposteria pan dulce' },
    { key: 'cookie', label: 'Galleta', keywords: 'galleta dulce postre' },
    { key: 'cake', label: 'Pastel', keywords: 'pastel torta tarta reposteria cumpleaños' },
    { key: 'dessert', label: 'Postre', keywords: 'postre dulce flan pudin' },
    { key: 'iceCream', label: 'Helado', keywords: 'helado nieve cono frio' },
    { key: 'iceCreamBowl', label: 'Helado en copa', keywords: 'helado copa sundae frio postre' },
    { key: 'candy', label: 'Dulces', keywords: 'dulce caramelo golosina' },
    { key: 'donut', label: 'Dona', keywords: 'dona rosquilla dulce' },
    { key: 'popcorn', label: 'Palomitas', keywords: 'palomitas snack botana' },
    { key: 'snack', label: 'Botanas', keywords: 'botana snack picar aperitivo' },
    { key: 'nuts', label: 'Frutos secos', keywords: 'nuez almendra fruto seco botana' },
    { key: 'fruit', label: 'Fruta', keywords: 'fruta manzana natural saludable' },
    { key: 'cherry', label: 'Cereza', keywords: 'cereza fruta topping' },
    { key: 'grape', label: 'Uva / vino', keywords: 'uva vino racimo fruta' },
    { key: 'citrus', label: 'Cítricos', keywords: 'citrico naranja limon jugo' },
    { key: 'vegetables', label: 'Verduras', keywords: 'verdura vegetal zanahoria guarnicion' },
    { key: 'carrot', label: 'Zanahoria / verdura', keywords: 'zanahoria verdura vegetal hortaliza carrot' },
    { key: 'pepper', label: 'Chile / especias', keywords: 'chile pimienta especia condimento sazonador picante pepper' },
    { key: 'sauce', label: 'Salsas', keywords: 'salsa aderezo liquido condimento gota botella sauce' },
    { key: 'grain', label: 'Cereales / granos', keywords: 'cereal grano trigo arroz pasta' },
    { key: 'grill', label: 'Parrilla / a la brasa', keywords: 'parrilla brasa fuego asado a la plancha' },
    { key: 'spicy', label: 'Picante', keywords: 'picante chile fuego caliente' },
    { key: 'snowflake', label: 'Congelados / frío', keywords: 'congelado frio refrigerado nieve hielo snowflake' },
    { key: 'package', label: 'Empaques', keywords: 'empaque paquete caja embalaje insumo package' },
    { key: 'vegan', label: 'Vegano', keywords: 'vegano plant based sin animal' },
    { key: 'vegetarian', label: 'Vegetariano', keywords: 'vegetariano verde hoja saludable' },
    { key: 'drinks', label: 'Bebidas', keywords: 'bebida bebidas refresco gaseosa jugo vaso trago drink sodas' },
    { key: 'coffee', label: 'Café', keywords: 'cafe caliente bebida cafeteria' },
    { key: 'tea', label: 'Té / infusión', keywords: 'te infusion caliente bebida' },
    { key: 'milk', label: 'Leche / lácteos', keywords: 'leche lacteo batido malteada' },
    { key: 'juice', label: 'Jugo natural', keywords: 'jugo zumo natural fresco licuado' },
    { key: 'soda', label: 'Refresco / gaseosa', keywords: 'refresco gaseosa soda bebida lata' },
    { key: 'water', label: 'Agua', keywords: 'agua botella mineral bebida' },
    { key: 'wine', label: 'Vino', keywords: 'vino copa licor bebida' },
    { key: 'beer', label: 'Cerveza', keywords: 'cerveza chela birra bebida' },
    { key: 'cocktail', label: 'Coctel', keywords: 'coctel trago licor bar bebida' },
    { key: 'dollar', label: 'Precio / promoción', keywords: 'precio promocion oferta dinero combo' },
    { key: 'tag', label: 'Etiqueta', keywords: 'etiqueta categoria general tag' },
    { key: 'list', label: 'Lista / menú', keywords: 'lista menu carta general' },
];
